import React, { useState } from 'react';
import { InlineInfoAlert } from '@bingads-webui-cc-react/alert';
import { InputFormGroup, LineByLineFormGroup } from '@bingads-webui-cc-react/layout';
import { ValidationGroup, Input } from '@bingads-webui-react/primitive-validation';
import { Constants, TaxUtils } from '@bingads-webui-clientcenter/entity-utils';
import { TaxOptionTypeRenderer } from '../controls/tax-option-type-renderer';
import { TaxTypeFormPropTypes } from '../shapes';
import { InputTaxFieldId, TaxOptionTypeKey, TaxOptionTypeI18nKey, LegalIdentifierNumberPilot, LegalIdentifierHintI18nKey, TaxFormMode, requiredFeatures } from '../constants';
import { validateField, ValidateItems, CustomValidateItems } from '../validator';
import { registerTaxform } from '../taxform-store';
import { BaseViewTaxForm } from './viewtaxforms/base-viewtaxform';
import {
  withViewTaxFormWithFilledEmptyTaxValue,
  getTaxOptionType,
  getHelpPopupTopicFromTaxSchema,
  getPilotStatus,
  addLegalIdentifierNumberInTaxSchema,
  onTaxFormValidateFillingLegalIdentifier,
  getTaxTypeLabel,
} from '../utils';
import { BrazilViewTaxForm } from './viewtaxforms/brazil-viewtaxform';
import { LegalIdentifierNumberField } from '../controls';
import { useValidationI18n } from '../hooks/use-i18n';

const {
  getTaxOptionLabel, isPersonalTaxOptionType: isPersonalTaxOptionTypeFunc, isCPFIdValid, isCNPJIdValid, isIEValid, isCCMValid, isCNPJIdV2Valid,
} = TaxUtils;
const {
  TaxLabelFormat, TaxType, TaxIdMaxLength: { CPF: maxLengthCPF, CNPJ: maxLengthCNPJ, CCM: maxLengthCCM }, CountryCode,
} = Constants;

// The condition to display the CCM field is Country 'BR' and City 'Sao Paulo'. StateOrProvince doesn't matter.
const pathRegex = /^\/BR\/[^/]*\/S(Ã|A[\u0303]?)O\sPAULO$/;
const isSaoPauloCity = path => pathRegex.test(path.toUpperCase());
const isBrazilCPFRequired = ({ isCPFRequired, permissions }) =>
  isCPFRequired || getPilotStatus(requiredFeatures.ABLBrazilCPFMandatory, permissions);

const getBrazilTaxSchema = ({
  isPersonalTaxOptionType,
  isCPFRequired,
  permissions,
  taxFormMode,
  shouldHideBRUpdateTaxForm,
  taxFormat,
}) => {
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);
  const IsABLBrazilNewCCMTaxPilotOn = getPilotStatus(requiredFeatures.ABLBrazilNewCCMTax, permissions);
  const isCPFEffectivelyRequired = isBrazilCPFRequired({ isCPFRequired, permissions });
  const taxSchema = {
    name: 'Brazil Taxation Validation Schema',
    type: 'object',
    properties: isPersonalTaxOptionType ? {
      [TaxType.CPF]: {
        type: 'string',
        maxLength: maxLengthCPF,
        required: isCPFEffectivelyRequired,
        helpPopupTopic: 'Page_Accounts_Details_BrazilTaxID',
      },
    } : {
      [TaxType.CNPJ]: {
        type: 'string',
        maxLength: maxLengthCNPJ,
        required: true,
        helpPopupTopic: 'Page_Accounts_Details_BrazilTaxID',
      },
    },
  };

  if (!isPersonalTaxOptionType && isSaoPauloCity(taxFormat)) {
    // eslint-disable-next-line no-param-reassign
    taxSchema.properties[TaxType.CCM] = {
      type: 'string',
      maxLength: maxLengthCCM,
      required: true,
    };
  }

  if (IsLegalIdentifierNumberPilotOn && !(taxFormMode === TaxFormMode.ViewMode)) {
    addLegalIdentifierNumberInTaxSchema(taxSchema);
  }

  if (IsABLBrazilNewCCMTaxPilotOn && !shouldHideBRUpdateTaxForm) { // IsABLBrazilNewCCMTaxPilotOn pilot on and not add funds for Boleto scenario
    // only support CCM for business customer and Sao Paulo city
    if (!isPersonalTaxOptionType && isSaoPauloCity(taxFormat)) {
      // eslint-disable-next-line no-param-reassign
      taxSchema.properties[TaxType.CCM] = {
        type: 'string',
        required: false,
      };
    }

    // eslint-disable-next-line no-param-reassign
    taxSchema.properties[TaxType.IE] = {
      type: 'string',
      required: false,
    };
  } else if (!shouldHideBRUpdateTaxForm) { // IsABLBrazilNewCCMTaxPilotOn pilot off and not add funds for Boleto scenario
    // eslint-disable-next-line no-param-reassign
    taxSchema.properties[TaxType.CCM] = {
      type: 'string',
      required: false,
    };

    // eslint-disable-next-line no-param-reassign
    taxSchema.properties[TaxType.IE] = {
      type: 'string',
      required: false,
    };
  }

  return taxSchema;
};

const BrazilTaxForm = (props) => {
  const {
    i18n,
    onValidate,
    data,
    mtData,
    showLineByLineFormGroup,
    disabled,
    alwaysShowError,
    isCPFRequired,
    permissions,
    shouldHideBRUpdateTaxForm,
    taxFormat,
  } = props;
  const validationI18n = useValidationI18n(i18n);
  const defaultTaxOptionTypeValue = getTaxOptionType({ uiObject: data, permissions });
  const mtTaxOptionTypeValue = getTaxOptionType({ uiObject: mtData, permissions });
  const CustomizedFormGroup = showLineByLineFormGroup ? LineByLineFormGroup : InputFormGroup;
  const [taxOptionType, setTaxOptionType] = useState(defaultTaxOptionTypeValue);
  const isStaticTaxOptionType = !!mtTaxOptionTypeValue;
  const isPersonalTaxOptionType = isPersonalTaxOptionTypeFunc(taxOptionType);
  const isCPFEffectivelyRequired = isBrazilCPFRequired({ isCPFRequired, permissions });
  const brazilTaxSchema = getBrazilTaxSchema({ isPersonalTaxOptionType, isCPFRequired, ...props });
  const taxLabelFormat = isCPFEffectivelyRequired ? TaxLabelFormat.Default : TaxLabelFormat.Optional;
  const IsLegalIdentifierNumberPilotOn = getPilotStatus(LegalIdentifierNumberPilot, permissions);
  const legalIdentifierNumberFieldVisuallyHidden = !isPersonalTaxOptionType;
  const IsABLBrazilNewCCMTaxPilotOn = getPilotStatus(requiredFeatures.ABLBrazilNewCCMTax, permissions);
  const isTaxFormUsingSaoPauloCity = isSaoPauloCity(taxFormat);
  const IsABLBrazilNewCNPJTaxPilotOn = getPilotStatus(requiredFeatures.ABLBrazilNewCNPJ, permissions);
  const taxInfoAlert = getPilotStatus(requiredFeatures.ABLBrazilCPFMandatory, permissions) && (
    <InlineInfoAlert>{i18n.getString(_TL_('To comply with Brazil\'s tax reform and maintain uninterrupted electronic invoicing (NF-e), please confirm your legal address, postal code (CEP), and tax ID (CPF or CNPJ) before 30 November 2026. Accurate tax information is required for invoice generation. Missing or invalid data may result in invoicing delays and could impact service delivery. Microsoft may verify and update taxpayer information using publicly available databases.'))}</InlineInfoAlert>
  );

  const onTaxFormValidate = IsLegalIdentifierNumberPilotOn
    ? newTaxData => onTaxFormValidateFillingLegalIdentifier({
      newTaxData,
      onValidate,
      prevTaxData: data,
      listenedToField: isPersonalTaxOptionType ? TaxType.CPF : TaxType.CNPJ,
      visuallyHidden: legalIdentifierNumberFieldVisuallyHidden,
    })
    : onValidate;

  return (
    <React.Fragment>
      {
        isStaticTaxOptionType ?
          <BaseViewTaxForm
            i18n={i18n}
            name={TaxOptionTypeKey}
            label={i18n.getString(TaxOptionTypeI18nKey)}
            value={getTaxOptionLabel(taxOptionType, i18n)}
          /> :
          <TaxOptionTypeRenderer
            i18n={i18n}
            taxOptionType={taxOptionType}
            showLineByLineFormGroup={showLineByLineFormGroup}
            onTaxOptionTypeChange={item => setTaxOptionType(item)}
            disabled={disabled}
          />
      }
      <ValidationGroup
        validationProps={{
          schema: brazilTaxSchema,
          i18n: validationI18n,
          onValidate: onTaxFormValidate,
          data,
        }}
      >
        {isPersonalTaxOptionType ?
          (
            <CustomizedFormGroup
              isStatic
              label={getTaxTypeLabel(TaxType.CPF, i18n, { labelFormat: taxLabelFormat })}
              key={TaxType.CPF}
              helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: brazilTaxSchema, taxType: TaxType.CPF })}
            >
              <Input
                id={InputTaxFieldId(TaxType.CPF)}
                validationProps={{
                  i18n: validationI18n,
                  field: TaxType.CPF,
                  validator: value => validateField(
                    value,
                    { [ValidateItems.Required]: { isRequired: isCPFEffectivelyRequired }, [ValidateItems.MaxLength]: maxLengthCPF },
                    { [CustomValidateItems.CPFValidation]: inputValue => inputValue.length > 0 && !isCPFIdValid(inputValue) },
                    i18n
                  ),
                  alwaysShowError,
                }}
                disabled={disabled}
              />
              {taxInfoAlert}
            </CustomizedFormGroup>
          ) :
          (
            <React.Fragment>
              <CustomizedFormGroup
                isStatic
                label={getTaxTypeLabel(TaxType.CNPJ, i18n)}
                key={TaxType.CNPJ}
                helpPopupTopic={getHelpPopupTopicFromTaxSchema({ taxSchema: brazilTaxSchema, taxType: TaxType.CNPJ })}
              >
                <Input
                  id={InputTaxFieldId(TaxType.CNPJ)}
                  validationProps={{
                  i18n: validationI18n,
                  field: TaxType.CNPJ,
                  validator: value => validateField(
                    value,
                    { [ValidateItems.Required]: { isRequired: true }, [ValidateItems.MaxLength]: maxLengthCNPJ },
                    IsABLBrazilNewCNPJTaxPilotOn ?
                      {
                        // when pilot on, use both old and new validation regex and v2 validation message
                        [CustomValidateItems.CNPJV2Validation]: inputValue => inputValue.length > 0 && (!isCNPJIdValid(inputValue) && !isCNPJIdV2Valid(inputValue)),
                      } :
                      {
                        // when pilot off, use old validation regex and message
                        [CustomValidateItems.CNPJValidation]: inputValue => inputValue.length > 0 && !isCNPJIdValid(inputValue),
                      },
                    i18n
                  ),
                  alwaysShowError,
                }}
                  disabled={disabled}
                />
                {taxInfoAlert}
              </CustomizedFormGroup>
              {isTaxFormUsingSaoPauloCity && shouldHideBRUpdateTaxForm &&
                <CustomizedFormGroup
                  isStatic
                  label={getTaxTypeLabel(TaxType.CCM, i18n)}
                  key={TaxType.CCM}
                >
                  <Input
                    id={InputTaxFieldId(TaxType.CCM)}
                    validationProps={{
                      i18n: validationI18n,
                      field: TaxType.CCM,
                      validator: value => validateField(
                        value,
                        {},
                        // when pilot on, use new validation regex, otherwise no validation
                        { [CustomValidateItems.BrazilCCMFormatValidation]: IsABLBrazilNewCCMTaxPilotOn ? inputValue => inputValue.length > 0 && !isCCMValid(inputValue) : false },
                        i18n
                      ),
                      alwaysShowError,
                    }}
                    disabled={disabled}
                  />
                </CustomizedFormGroup>
              }
            </React.Fragment>
          )
        }
        {IsLegalIdentifierNumberPilotOn &&
          <LegalIdentifierNumberField
            i18n={i18n}
            hintContent={i18n.getString(LegalIdentifierHintI18nKey[CountryCode.BR])}
            showLineByLineFormGroup
            alwaysShowError={alwaysShowError}
            disabled={disabled}
            visuallyHidden={legalIdentifierNumberFieldVisuallyHidden}
          />}
        {!shouldHideBRUpdateTaxForm &&
          <React.Fragment>
            {(!IsABLBrazilNewCCMTaxPilotOn || (IsABLBrazilNewCCMTaxPilotOn && isTaxFormUsingSaoPauloCity && !isPersonalTaxOptionType)) &&
              <CustomizedFormGroup
                isStatic
                label={getTaxTypeLabel(TaxType.CCM, i18n, { labelFormat: TaxLabelFormat.Optional })}
                key={TaxType.CCM}
              >
                <Input
                  id={InputTaxFieldId(TaxType.CCM)}
                  validationProps={{
                    i18n: validationI18n,
                    field: TaxType.CCM,
                    validator: value => validateField(
                      value,
                      {},
                      // when pilot on, use new validation regex, otherwise no validation
                      { [CustomValidateItems.BrazilCCMFormatValidation]: IsABLBrazilNewCCMTaxPilotOn ? inputValue => inputValue.length > 0 && !isCCMValid(inputValue) : false },
                      i18n
                    ),
                    alwaysShowError,
                  }}
                  disabled={disabled}
                />
              </CustomizedFormGroup>
            }
            <CustomizedFormGroup
              isStatic
              label={getTaxTypeLabel(TaxType.IE, i18n, { labelFormat: TaxLabelFormat.StateTaxIDOptional })}
              key={TaxType.IE}
            >
              <Input
                id={InputTaxFieldId(TaxType.IE)}
                validationProps={{
                i18n: validationI18n,
                field: TaxType.IE,
                validator: value => validateField(
                  value,
                  {},
                  { [CustomValidateItems.IEValidation]: inputValue => inputValue.length > 0 && !isIEValid(inputValue) },
                  i18n
                ),
                alwaysShowError,
              }}
                disabled={disabled}
              />
            </CustomizedFormGroup>
          </React.Fragment>
        }
      </ValidationGroup>
    </React.Fragment>
  );
};

BrazilTaxForm.propTypes = TaxTypeFormPropTypes;

registerTaxform('/BR/', BrazilTaxForm, withViewTaxFormWithFilledEmptyTaxValue({
  ViewTaxForm: BrazilViewTaxForm,
  getTaxSchema: (props) => {
    const { data, permissions } = props;
    const taxOptionType = getTaxOptionType({ uiObject: data, permissions });
    const isPersonalTaxOptionType = isPersonalTaxOptionTypeFunc(taxOptionType);
    return getBrazilTaxSchema({ isPersonalTaxOptionType, ...props });
  },
}));
